/*
 * Roles, trades and money in cents.
 */

/** What a company does. Decides which features and words it gets (only The Cleaners has cleaners; the others have a crew). */
export const TRADES = ["cleaning", "lawn", "washers", "agency", "hq"] as const;
export type Trade = (typeof TRADES)[number];

/** A login's role in one company, the same roles as the WordPress Steward. */
export const ROLES = ["owner", "manager", "dispatch", "crew", "franchise_owner"] as const;
export type Role = (typeof ROLES)[number];

/** Money is kept as whole cents in code and as numeric in Postgres, never as floating point dollars. */
export type Cents = number & { readonly __cents: unique symbol };

/** "140.00", 140 or 140.005 to whole cents, rounding half away from zero (140.005 is 14001 cents). */
export function toCents(amount: string | number): Cents {
  const n = typeof amount === "number" ? amount : Number(amount);
  if (!Number.isFinite(n)) throw new Error(`Not an amount of money: ${String(amount)}`);
  const scaled = Math.abs(n) * 100;
  // Nudge past floating point edges like 1.005 * 100 = 100.49999 before rounding.
  const cents = Math.round(scaled + 1e-7) * Math.sign(n);
  return (cents === 0 ? 0 : cents) as Cents;
}

/** Whole cents to the "140.00" form Postgres numeric columns take. */
export function centsToDecimal(cents: Cents): string {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  return `${sign}${Math.trunc(abs / 100)}.${String(abs % 100).padStart(2, "0")}`;
}

/** Adds amounts in cents exactly. */
export function sumCents(values: readonly Cents[]): Cents {
  return values.reduce<number>((a, b) => a + b, 0) as Cents;
}

/** "$1,234.50" for people to read. */
export function formatMoney(cents: Cents): string {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  return `${sign}$${Math.trunc(abs / 100).toLocaleString("en-US")}.${String(abs % 100).padStart(2, "0")}`;
}

/**
 * Splits money in cents by weights so the parts always add up to the whole. Each part is rounded down, then the leftover
 * cents go one each to the first parts in order. With no weight at all, it splits evenly.
 */
export function splitCents(total: Cents, weights: readonly number[]): Cents[] {
  if (!Number.isInteger(total)) throw new Error("splitCents takes whole cents.");
  if (weights.length === 0) return [];
  if (weights.some((w) => !Number.isFinite(w) || w < 0)) throw new Error("Weights must be zero or more.");
  const sum = weights.reduce((a, b) => a + b, 0);
  const ws = sum > 0 ? weights : weights.map(() => 1);
  const wsum = sum > 0 ? sum : ws.length;
  const sign = total < 0 ? -1 : 1;
  const abs = Math.abs(total);
  const parts = ws.map((w) => Math.floor((abs * w) / wsum));
  let left = abs - parts.reduce((a, b) => a + b, 0);
  for (let i = 0; left > 0; i = (i + 1) % parts.length) {
    if (ws[i]! > 0) {
      parts[i]! += 1;
      left -= 1;
    }
  }
  return parts.map((p) => (p === 0 ? 0 : p * sign) as Cents);
}
